import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/PageStates";
import { ledger, child, formatDate } from "@/lib/mock-data";

export const Route = createFileRoute("/app/ledger")({
  component: LedgerPage,
});

const kindLabel: Record<string, { text: string; bg: string; fg: string }> = {
  earn: { text: "获得", bg: "var(--accent-soft)", fg: "var(--accent)" },
  redeem: { text: "兑换", bg: "var(--surface-2)", fg: "var(--muted)" },
  adjust: { text: "调整", bg: "var(--surface-2)", fg: "var(--muted)" },
  expire: { text: "过期", bg: "var(--warn-soft)", fg: "var(--warn-fg)" },
  restore: { text: "恢复", bg: "var(--accent-soft)", fg: "var(--accent)" },
};

function LedgerPage() {
  return (
    <div className="pb-6">
      <PageHeader title="台账" />
      <section className="px-5 pt-5">
        <p className="text-[var(--fs-sm)]" style={{ color: "var(--muted)" }}>
          当前可用点数
        </p>
        <p className="text-[2.25rem] font-semibold leading-none tracking-tight">
          {child.availablePoints}
        </p>
        <div className="mt-4 grid grid-cols-3 gap-3">
          <Stat label="即将过期" value={child.expiringSoon} tone="warn" />
          <Stat label="累计获得" value={child.totalEarned} />
          <Stat label="累计兑换" value={child.totalRedeemed} />
        </div>
      </section>

      <section className="mt-6 px-4">
        <h2 className="mb-2 px-1 text-[var(--fs-sm)] font-semibold">明细</h2>
        <ul className="space-y-2">
          {ledger.map((l) => {
            const k = kindLabel[l.kind];
            return (
              <li
                key={l.id}
                className="rounded-[var(--r-md)] border bg-surface p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className="rounded-full px-2 py-0.5 text-[var(--fs-xs)]"
                        style={{ background: k.bg, color: k.fg }}
                      >
                        {k.text}
                      </span>
                      <p className="truncate text-[var(--fs-sm)] font-medium">
                        {l.source}
                      </p>
                    </div>
                    <p
                      className="mt-1 text-[var(--fs-xs)]"
                      style={{ color: "var(--muted)" }}
                    >
                      {formatDate(l.createdAt)}
                      {l.expiresAt ? ` · 过期 ${formatDate(l.expiresAt)}` : ""}
                    </p>
                  </div>
                  <span
                    className="shrink-0 text-[var(--fs-md)] font-semibold"
                    style={{
                      color: l.delta > 0 ? "var(--success)" : "var(--danger)",
                    }}
                  >
                    {l.delta > 0 ? "+" : ""}
                    {l.delta}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "warn";
}) {
  return (
    <div
      className="rounded-[var(--r-md)] border bg-surface p-3"
      style={
        tone === "warn"
          ? { background: "var(--warn-soft)", borderColor: "var(--warn)" }
          : undefined
      }
    >
      <p
        className="text-[var(--fs-xs)]"
        style={{ color: tone === "warn" ? "var(--warn-fg)" : "var(--muted)" }}
      >
        {label}
      </p>
      <p
        className="mt-1 text-[var(--fs-lg)] font-semibold"
        style={tone === "warn" ? { color: "var(--warn-fg)" } : undefined}
      >
        {value}
      </p>
    </div>
  );
}
